import { html, nothing, render } from 'lit-html';

import { applyIconSize, applyIconVariant } from '@disy/lib/icon';
import alertStyles from '@disy/ui/alert.css' with { type: 'css' };
import { createAnchorButton } from '@disy/ui/button';
import iconStyles from '@disy/ui/icon.css' with { type: 'css' };

import anchorStyles from 'ui/anchor.css' with { type: 'css' };
import { createAttributeLabel } from 'ui/attribute-label/attribute-label';
import ellipsisStyles from 'ui/ellipsis/ellipsis.css' with { type: 'css' };

import { getStore } from 'cadenza/store/cadenza-store';
import { openAdvancedDataSettings } from 'cadenza/workbook/advanced-data-settings-dialog/advanced-data-settings-dialog-utils';
import type { DataViewVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import { visualizationIcon } from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import { viewTypeIcon } from 'cadenza/workbook/api/view-type';
import { getSpatialConditionItemValues } from 'cadenza/workbook/conditions/condition-util';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import {
  getAttributeById,
  getAttributesByConditionId,
  getConditionById,
  getDataViewById,
  isActiveView,
  supportsMultipleDataView,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { compareDataViewsOfSameViewByAppearance } from 'cadenza/workbook/workbook-utils';

import styles from './affected-view-tile.module.css' with { type: 'css' };
import i18n from './affected-view-tile.properties';

const COMPONENT_NAME = 'd-affected-view-tile';

export class AffectedViewTile extends HTMLElement {
  override shadowRoot!: ShadowRoot;
  readonly #view: ViewDto;
  readonly #conditionId?: ConditionId;

  constructor(view: ViewDto, conditionId?: ConditionId) {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.adoptedStyleSheets = [alertStyles, anchorStyles, iconStyles, ellipsisStyles, styles];
    this.#view = view;
    this.#conditionId = conditionId;
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.#render();
  }

  #render() {
    render(this.#template(), this.shadowRoot);
  }

  #template() {
    if (supportsMultipleDataView(getStore().getState(), this.#view.id)) {
      return html`<div class="title ${this.#affectedActiveViewAttributeString()}">
          ${this.#viewIcon()}<span class="ellipsis">${this.#view.name}</span>
        </div>
        <div class="contents">
          ${this.#view.dataViews
            .toSorted((dv1, dv2) => compareDataViewsOfSameViewByAppearance(dv1, dv2, this.#view))
            .map((dataViewId) => getDataViewById(getStore().getState(), dataViewId))
            .map(
              (dataView) =>
                html`<span class="layer">
                  <div class="title">
                    ${this.#visualizationIcon(dataView.visualizationKey)}<span class="ellipsis">${dataView.name}</span>
                  </div>
                  ${this.#optionalRelevantGeometryInfo(dataView.id)}
                </span>`,
            )}
        </div>`;
    } else {
      const dataView = getDataViewById(getStore().getState(), this.#view.dataViews[0]);
      return html`<div class="title ${this.#affectedActiveViewAttributeString()}">
          ${this.#visualizationIcon(dataView.visualizationKey)}<span class="ellipsis">${this.#view.name}</span>
        </div>
        ${this.#optionalRelevantGeometryInfo(dataView.id)}`;
    }
  }

  #affectedActiveViewAttributeString() {
    return isActiveView(getStore().getState(), this.#view.id) ? 'affected-active-view' : '';
  }

  #viewIcon() {
    return viewTypeIcon(this.#view.type, { size: 's', variant: 'normal' });
  }

  #visualizationIcon(visualizationKey: DataViewVisualizationKey) {
    const dataViewIcon = visualizationIcon(visualizationKey);
    applyIconSize(dataViewIcon, 's');
    applyIconVariant(dataViewIcon, 'normal');
    return dataViewIcon;
  }

  #showRelatedGeometryHint(condition?: ConditionDto) {
    return condition?.type === 'PolygonCondition' && getSpatialConditionItemValues(condition).length > 0;
  }

  #optionalRelevantGeometryInfo(dataViewId: DataViewId) {
    const condition = this.#conditionId ? getConditionById(getStore().getState(), this.#conditionId) : undefined;
    if (condition && this.#showRelatedGeometryHint(condition)) {
      const attributeId = getAttributesByConditionId(getStore().getState(), condition.id)[dataViewId][0];
      const attribute = getAttributeById(getStore().getState(), attributeId);
      return html`<d-alert class="d-alert-small d-alert-info">
        ${i18n('affected-view-tile.related-geometry')}
        ${createAttributeLabel(attribute.type, attribute.printName, attribute)}
        <br />
        ${this.#adjustGeometryAttributeButton(dataViewId)}
      </d-alert>`;
    }
    return nothing;
  }

  #adjustGeometryAttributeButton(targetDataViewId: DataViewId) {
    if (!isWorkbookFeatureAvailable(getStore().getState(), 'spatial-condition-management')) {
      return [];
    }
    return [
      createAnchorButton(i18n('affected-view-tile.related-geometry.button'), {
        onclick: () => openAdvancedDataSettings(targetDataViewId),
      }),
    ];
  }
}

customElements.define(COMPONENT_NAME, AffectedViewTile);
